import {
  cloneElement,
  createContext,
  useContext,
  useEffect,
  useState,
  ReactElement,
} from "react";
import { TAuthContext, TCredentials, TUser } from "../types/context";
import {
  decodeUser,
  getCredentials,
  removeCredentials,
  setCredentials,
} from "../utils/helpers";
import { useIdleTimer } from "react-idle-timer";
import { IDLE_TIMEOUT, LAST_ACTIVE_KEY } from "../utils/constants";

const markActive = () => {
  try {
    localStorage.setItem(LAST_ACTIVE_KEY, String(Date.now()));
  } catch (e) {}
};

const idleTooLong = () => {
  try {
    const lastActive = Number(localStorage.getItem(LAST_ACTIVE_KEY));
    return !lastActive || Date.now() - lastActive >= IDLE_TIMEOUT;
  } catch (e) {
    return false;
  }
};

const AuthContext = createContext<TAuthContext>({
  loggedIn: false,
  login: () => {},
  logout: () => {},
  updateUser: () => {},
  user: null,
  gettingAuth: true,
  authToken: "",
});

const AuthProvider = ({ children }: { children: ReactElement }) => {
  const [loggedIn, setLoggedIn] = useState(false);
  const [user, setUser] = useState<TUser | null>(null);
  const [authToken, setAuthToken] = useState<string>("");
  const [gettingAuth, setGettingAuth] = useState(true);

  useEffect(() => {
    const fetchCredentials = () => {
      const res = getCredentials();

      if (res && idleTooLong()) {
        removeCredentials();
      } else if (res) {
        markActive();
        setLoggedIn(true);
        setUser(decodeUser(res.access));
        setAuthToken(res.access);
      }
      setGettingAuth(false);
    };

    fetchCredentials();
  }, []);

  const login = (credentials: TCredentials) => {
    setCredentials(credentials);
    markActive();
    setUser(decodeUser(credentials.access));
    setAuthToken(credentials.access);
    setLoggedIn(true);
  };

  const logout = () => {
    removeCredentials();
    setUser(null);
    setLoggedIn(false);
  };

  useIdleTimer({
    timeout: IDLE_TIMEOUT,
    disabled: !loggedIn,
    crossTab: true,
    throttle: 5000,
    onAction: markActive,
    onIdle: logout,
  });

  useEffect(() => {
    if (!loggedIn) return;

    const checkIdle = () => {
      if (document.visibilityState === "visible" && idleTooLong()) logout();
    };
    document.addEventListener("visibilitychange", checkIdle);
    window.addEventListener("focus", checkIdle);

    return () => {
      document.removeEventListener("visibilitychange", checkIdle);
      window.removeEventListener("focus", checkIdle);
    };
  }, [loggedIn]);

  const updateUser = (user: TUser) => {
    setCredentials({ access: authToken, user });
    setUser(user);
  };

  const renderBody = (ele: ReactElement) => cloneElement(ele);

  return (
    <AuthContext.Provider
      value={{
        login,
        loggedIn: loggedIn,
        // loggedIn: true,
        logout,
        user,
        gettingAuth,
        authToken,
        updateUser,
      }}
    >
      {renderBody(children)}
    </AuthContext.Provider>
  );
};

const useAuth = () => useContext(AuthContext);

const LogoutRoute = () => <>{useAuth().logout()}</>;

export { AuthProvider, useAuth, LogoutRoute };
